import React from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import { 
  FaMobileAlt, 
  FaBolt, 
  FaStar, 
  FaShieldAlt, 
  FaGamepad,
  FaPuzzlePiece,
  FaRunning,
  FaCar,
  FaChild,
  FaBriefcase,
  FaTools,
  FaFilm,
  FaSearch,
  FaHeart,
  FaRocket,
  FaCheckCircle,
  FaSync,
  FaUsers,
  FaGlobe,
  FaClock,
  FaQuestionCircle,
  FaChevronDown
} from 'react-icons/fa';

const LandingPage = () => {
  const { t } = useTranslation();

  const categories = [
    { key: 'arcade', icon: FaGamepad },
    { key: 'puzzle', icon: FaPuzzlePiece },
    { key: 'action', icon: FaRunning },
    { key: 'racing', icon: FaCar },
    { key: 'kids', icon: FaChild },
    { key: 'productivity', icon: FaBriefcase },
    { key: 'utilities', icon: FaTools },
    { key: 'entertainment', icon: FaFilm }
  ];

  return (
    <div className="landing-page">
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">Appizoni</div>
            <LanguageSelector />
          </div>
        </div>
      </header>

      <section className="hero">
        <div className="container">
          <h1 className="hero-title">{t('hero.title')}</h1>
          <p className="hero-subtitle">{t('hero.subtitle')}</p>
          <p className="hero-description">{t('hero.description')}</p>
          <a href="/s/" className="cta-button primary">
            {t('buttons.explore')}
          </a>
        </div>
      </section>

      <section className="why-choose">
        <div className="container">
          <h2 className="section-title">{t('whyChoose.title')}</h2>
          <p className="section-subtitle">{t('whyChoose.subtitle')}</p>
          <div className="features-grid">
            <div className="feature-card feature-0">
              <div className="feature-icon-wrapper">
                <FaMobileAlt className="feature-icon" />
              </div>
              <h3 className="feature-title">{t('whyChoose.wideSelection.title')}</h3>
              <p className="feature-description">{t('whyChoose.wideSelection.description')}</p>
            </div>
            <div className="feature-card feature-1">
              <div className="feature-icon-wrapper">
                <FaBolt className="feature-icon" />
              </div>
              <h3 className="feature-title">{t('whyChoose.fastAccess.title')}</h3>
              <p className="feature-description">{t('whyChoose.fastAccess.description')}</p>
            </div>
            <div className="feature-card feature-2">
              <div className="feature-icon-wrapper">
                <FaStar className="feature-icon" />
              </div>
              <h3 className="feature-title">{t('whyChoose.highlyRated.title')}</h3>
              <p className="feature-description">{t('whyChoose.highlyRated.description')}</p>
            </div>
            <div className="feature-card feature-3">
              <div className="feature-icon-wrapper">
                <FaShieldAlt className="feature-icon" />
              </div>
              <h3 className="feature-title">{t('whyChoose.safeReliable.title')}</h3>
              <p className="feature-description">{t('whyChoose.safeReliable.description')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="catalog">
        <div className="container">
          <h2 className="section-title">{t('catalog.title')}</h2>
          <p className="section-subtitle">{t('catalog.subtitle')}</p>
          <div className="categories-grid">
            {categories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <div key={category.key} className={`category-card category-${index}`}>
                  <div className="category-icon-wrapper">
                    <IconComponent className="category-icon" />
                  </div>
                  <span className="category-name">{t(`catalog.categories.${category.key}`)}</span>
                </div>
              );
            })}
          </div>
          <a href="/s/" className="cta-button secondary">
            {t('catalog.cta')}
          </a>
        </div>
      </section>

      <section className="how-it-works">
        <div className="container">
          <h2 className="section-title">{t('howItWorks.title')}</h2>
          <p className="section-subtitle">{t('howItWorks.subtitle')}</p>
          <div className="steps">
            <div className="step">
              <div className="step-number-wrapper">
                <div className="step-number">1</div>
                <FaSearch className="step-icon" />
              </div>
              <h3 className="step-title">{t('howItWorks.step1.title')}</h3>
              <p className="step-description">{t('howItWorks.step1.description')}</p>
            </div>
            <div className="step">
              <div className="step-number-wrapper">
                <div className="step-number">2</div>
                <FaHeart className="step-icon" />
              </div>
              <h3 className="step-title">{t('howItWorks.step2.title')}</h3>
              <p className="step-description">{t('howItWorks.step2.description')}</p>
            </div>
            <div className="step">
              <div className="step-number-wrapper">
                <div className="step-number">3</div>
                <FaRocket className="step-icon" />
              </div>
              <h3 className="step-title">{t('howItWorks.step3.title')}</h3>
              <p className="step-description">{t('howItWorks.step3.description')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="benefits">
        <div className="container">
          <h2 className="section-title">{t('benefits.title')}</h2>
          <p className="section-subtitle">{t('benefits.subtitle')}</p>
          <div className="benefits-grid">
            <div className="benefit-card">
              <FaCheckCircle className="benefit-icon" />
              <h3 className="benefit-title">{t('benefits.benefit1.title')}</h3>
              <p className="benefit-description">{t('benefits.benefit1.description')}</p>
            </div>
            <div className="benefit-card">
              <FaSync className="benefit-icon" />
              <h3 className="benefit-title">{t('benefits.benefit2.title')}</h3>
              <p className="benefit-description">{t('benefits.benefit2.description')}</p>
            </div>
            <div className="benefit-card">
              <FaMobileAlt className="benefit-icon" />
              <h3 className="benefit-title">{t('benefits.benefit3.title')}</h3>
              <p className="benefit-description">{t('benefits.benefit3.description')}</p>
            </div>
            <div className="benefit-card">
              <FaGlobe className="benefit-icon" />
              <h3 className="benefit-title">{t('benefits.benefit4.title')}</h3>
              <p className="benefit-description">{t('benefits.benefit4.description')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="stats">
        <div className="container">
          <h2 className="section-title">{t('stats.title')}</h2>
          <p className="section-subtitle">{t('stats.subtitle')}</p>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{t('stats.stat1.number')}</div>
              <div className="stat-label">{t('stats.stat1.label')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{t('stats.stat2.number')}</div>
              <div className="stat-label">{t('stats.stat2.label')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{t('stats.stat3.number')}</div>
              <div className="stat-label">{t('stats.stat3.label')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{t('stats.stat4.number')}</div>
              <div className="stat-label">{t('stats.stat4.label')}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="security">
        <div className="container">
          <div className="security-content">
            <div className="security-icon-wrapper">
              <FaShieldAlt className="security-icon" />
            </div>
            <h2 className="section-title">{t('security.title')}</h2>
            <p className="section-subtitle">{t('security.description')}</p>
          </div>
        </div>
      </section>

      <section className="faq">
        <div className="container">
          <h2 className="section-title">{t('faq.title')}</h2>
          <p className="section-subtitle">{t('faq.subtitle')}</p>
          <div className="faq-list">
            <div className="faq-item">
              <div className="faq-question">
                <FaQuestionCircle className="faq-icon" />
                <h3>{t('faq.q1.question')}</h3>
              </div>
              <p className="faq-answer">{t('faq.q1.answer')}</p>
            </div>
            <div className="faq-item">
              <div className="faq-question">
                <FaQuestionCircle className="faq-icon" />
                <h3>{t('faq.q2.question')}</h3>
              </div>
              <p className="faq-answer">{t('faq.q2.answer')}</p>
            </div>
            <div className="faq-item">
              <div className="faq-question">
                <FaQuestionCircle className="faq-icon" />
                <h3>{t('faq.q3.question')}</h3>
              </div>
              <p className="faq-answer">{t('faq.q3.answer')}</p>
            </div>
            <div className="faq-item">
              <div className="faq-question">
                <FaQuestionCircle className="faq-icon" />
                <h3>{t('faq.q4.question')}</h3>
              </div>
              <p className="faq-answer">{t('faq.q4.answer')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="final-cta">
        <div className="container">
          <h2 className="section-title">{t('cta.title')}</h2>
          <p className="section-subtitle">{t('cta.subtitle')}</p>
          <a href="/s/" className="cta-button primary large">
            {t('buttons.getStarted')}
          </a>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-contact">
            <h3 className="footer-contact-title">{t('footer.customerService')}</h3>
            <div className="footer-contact-info">
              <p className="footer-contact-item">
                <span className="footer-label">{t('footer.email')}</span>
                <a href={`mailto:${t('footer.emailValue')}`} className="footer-link">{t('footer.emailValue')}</a>
              </p>
              <p className="footer-contact-item">
                <span className="footer-label">{t('footer.phone')}</span>
                <a href={`tel:${t('footer.phoneValue')}`} className="footer-link">{t('footer.phoneValue')}</a>
              </p>
              <p className="footer-contact-item">
                <span className="footer-label">{t('footer.serviceProvider')}</span>
                <span className="footer-value">{t('footer.serviceProviderValue')}</span>
              </p>
            </div>
          </div>
          <div className="footer-links">
            <a href="/kontakt.html" className="footer-link-item">{t('footer.links.kontakt')}</a>
            <span className="footer-link-separator">|</span>
            <a href="/impressum.html" className="footer-link-item">{t('footer.links.impressum')}</a>
            <span className="footer-link-separator">|</span>
            <a href="/terms.html" className="footer-link-item">{t('footer.links.terms')}</a>
          </div>
          <p className="footer-text">{t('footer.text')}</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;

